import { api, type EndOfManagement } from '../api';
import { callout, field, input, openDialog, todayISO, val } from '../dialog';
import { esc, html } from '../ui';

/**
 * Taking an aircraft off the fleet.
 *
 * Deliberately not called "delete". The management contract ends; the
 * continuing airworthiness record does not. ML.A.305(h) makes the record the
 * aircraft's rather than the organisation's — it is transferred to the owner or
 * to the next CAMO, with the detailed maintenance records going back 36 months.
 * So the dialog asks the two questions that have to be answerable at the next
 * audit, and says plainly what is being kept.
 *
 * Outright deletion is offered only where nothing has accumulated at all,
 * which is the one case where there is no record to preserve: an aircraft added
 * by mistake.
 */
export function endManagement(ctx: EndOfManagement, onDone: () => void): void {
  const kept: Array<[string, number]> = [
    ['Flights', ctx.records.flights],
    ['Task compliances', ctx.records.taskCompliances],
    ['Directive compliances', ctx.records.directiveCompliances],
    ['Certificates of release', ctx.records.certificatesOfRelease],
    ['Airworthiness certificates', ctx.records.certificates],
    ['Component fitments', ctx.records.componentFitments],
  ];

  if (ctx.deletable) {
    openDialog<Record<string, unknown>>({
      title: `Remove ${ctx.registration}`,
      sub: 'Nothing has been recorded against this aircraft',
      primary: 'Remove it',
      body: () => html`
        ${callout('info', 'i', html`
          <b>${esc(ctx.registration)}</b> has no flights, compliances, releases or certificates against it,
          so there is no continuing airworthiness record to preserve. It can be removed outright — which is
          only ever the right answer for an aircraft added by mistake.
          <br><br>
          Once it has any history, this stops being offered and the aircraft is taken off the fleet
          instead, keeping its record.
        `)}
        ${field('Type the registration to confirm',
          input('confirm', '', `placeholder="${esc(ctx.registration)}" style="font-family:var(--mono);text-transform:uppercase" maxlength="12"`))}
      `,
      collect: (root) => {
        if (val(root, 'confirm').trim().toUpperCase() !== ctx.registration.toUpperCase()) {
          return { error: `Type ${ctx.registration} to confirm.` };
        }
        return {};
      },
      submit: () => api.aircraft.remove(ctx.aircraftId),
      done: (r) => `${r.registration} removed`,
      onDone,
    });
    return;
  }

  openDialog<{ reason: string; endedAt?: string; recordsTransferredTo: string }>({
    wide: true,
    title: `End management of ${ctx.registration}`,
    sub: `${ctx.owner} — the aircraft leaves the fleet, its record stays`,
    primary: 'End management',

    body: () => html`
      ${ctx.blockers.length
        ? callout('exceed', '✕', html`
            <b>Not yet.</b>
            <ul style="margin:6px 0 0;padding-left:18px">
              ${ctx.blockers.map((b) => `<li>${esc(b)}</li>`).join('')}
            </ul>
          `)
        : ''}

      ${ctx.warnings.length
        ? callout('caution', '!', html`
            <ul style="margin:0;padding-left:18px">
              ${ctx.warnings.map((w) => `<li>${esc(w)}</li>`).join('')}
            </ul>
          `)
        : ''}

      <div class="fset">
        <div class="lg">The handover</div>
        ${field('Why management ended',
          input('reason', '', 'placeholder="Sold; the new owner appointed another CAO" maxlength="400"'),
          'The first thing asked at the next audit')}
        ${field('Records transferred to',
          input('recordsTransferredTo', '', 'placeholder="Aeroclub Cluj CAO.RO.0099 — by hand, receipt signed" maxlength="200"'),
          'ML.A.305(h) — the records go to the owner or to the next organisation; an unrecorded handover is the same as no handover')}
        ${field('Management ended on', input('endedAt', todayISO(), 'type="date"'))}
      </div>

      <div class="fset">
        <div class="lg">What is kept</div>
        ${callout('info', 'i', html`
          Nothing below is deleted. ${esc(ctx.registration)} stops appearing in the fleet, the due list and
          the reports, and everything recorded against it stays exactly where it is — because the record
          has to outlive the contract and travel with the aircraft.
        `)}
        <div class="rows">
          ${kept
            .filter(([, n]) => n > 0)
            .map(([label, n]) => html`
              <div class="row">
                <div class="bd"><div class="t">${esc(label)}</div></div>
                <div class="m" style="font-family:var(--mono)">${n}</div>
              </div>
            `)
            .join('')}
        </div>
      </div>
    `,

    collect: (root) => {
      if (ctx.blockers.length) {
        return { error: ctx.blockers[0] };
      }
      const reason = val(root, 'reason').trim();
      const recordsTransferredTo = val(root, 'recordsTransferredTo').trim();
      if (!reason) return { error: 'Say why management ended.' };
      if (!recordsTransferredTo) {
        return { error: 'Name who the continuing airworthiness records were transferred to — ML.A.305(h) requires it.' };
      }
      return { reason, recordsTransferredTo, endedAt: val(root, 'endedAt') || undefined };
    },

    submit: (payload) => api.aircraft.archive(ctx.aircraftId, payload),
    done: (r) => `${r.registration} off the fleet — ${r.recordsRetained} records retained and handed to ${r.recordsTransferredTo}`,
    onDone,
  });
}
